import { io, Socket } from 'socket.io-client';

// WebSocket连接地址：生产环境去掉/api后缀，开发环境直接连接后端
const getSocketUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3010';

  // 生产环境：https://kanban.mengdong.video/api -> https://kanban.mengdong.video
  // 开发环境：http://localhost:3010 -> http://localhost:3010
  if (apiUrl.endsWith('/api')) {
    return apiUrl.slice(0, -4); // 去掉 /api
  }

  return apiUrl;
};

const SOCKET_URL = getSocketUrl();

export enum TaskEventType {
  TASK_CREATED = 'task:created',
  TASK_UPDATED = 'task:updated',
  TASK_DELETED = 'task:deleted',
  TASK_REORDERED = 'task:reordered',
  COMMENT_CREATED = 'task:comment-created',
}

export interface TaskEvent {
  task?: any;
  taskId?: string;
  taskIds?: string[];
  comment?: any;
  operatorId: string;
  timestamp: string;
}

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  /**
   * 初始化WebSocket连接
   */
  connect(token: string): Socket {
    if (this.socket?.connected) {
      console.log('WebSocket已连接，跳过重复连接');
      return this.socket;
    }

    this.socket = io(SOCKET_URL, {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    });

    this.setupEventListeners();

    return this.socket;
  }

  /**
   * 设置基础事件监听器
   */
  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      // WebSocket断开连接 (正常情况，如HMR热更新)
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ WebSocket连接错误:', error.message);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('达到最大重连次数，停止尝试重连');
        this.socket?.disconnect();
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 WebSocket重连成功 (尝试 ${attemptNumber} 次)`);
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ WebSocket重连失败');
    });
  }

  /**
   * 加入工作空间房间
   */
  joinWorkspace(workspace: 'personal' | 'team', teamId?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('WebSocket未连接'));
        return;
      }

      this.socket.emit('join-workspace', { workspace, teamId }, (response: any) => {
        if (response?.success) {
          resolve(response);
        } else {
          console.error('加入房间失败:', response?.error);
          reject(new Error(response?.error || '加入房间失败'));
        }
      });
    });
  }

  /**
   * 离开工作空间房间
   */
  leaveWorkspace(workspace: 'personal' | 'team', teamId?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('WebSocket未连接'));
        return;
      }

      this.socket.emit('leave-workspace', { workspace, teamId }, (response: any) => {
        if (response?.success) {
          resolve(response);
        } else {
          console.error('离开房间失败:', response?.error);
          reject(new Error(response?.error || '离开房间失败'));
        }
      });
    });
  }

  /**
   * 监听任务事件
   */
  onTaskEvent(eventType: TaskEventType, callback: (data: TaskEvent) => void) {
    if (!this.socket) return;

    this.socket.on(eventType, callback);
  }

  /**
   * 移除任务事件监听
   */
  offTaskEvent(eventType: TaskEventType, callback?: (data: TaskEvent) => void) {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(eventType, callback);
    } else {
      this.socket.off(eventType);
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * 获取连接状态
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * 获取socket实例
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

// 导出单例
export const websocketService = new WebSocketService();
